import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axios';
import { Link } from 'react-router-dom';
import { Box, Image, Text, IconButton, Grid, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { FaUserAlt } from 'react-icons/fa';

const TopArtists = ({ toggler }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchTopArtists();
    }, []);

    const fetchTopArtists = () => {
        setLoading(true);
        axiosInstance
            .get("/statistics/get_top_artists")
            .then((res) => {
                setData(res.data);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Create columns with 3 items each
    const createColumns = (items) => {
        const columns = [];
        for (let i = 0; i < items.length; i += 3) {
            columns.push(items.slice(i, i + 3));
        }
        return columns;
    };

    // Loading skeleton component
    const LoadingSkeleton = () => {
        const skeletonItems = Array(9).fill(0);
        const skeletonColumns = createColumns(skeletonItems);
        
        return (
            <Box
                display={{ base: "flex", md: "grid" }}
                gridTemplateColumns={{ md: "repeat(auto-fit, minmax(250px, 300px))", lg: "repeat(auto-fit, minmax(250px, 300px))", xl: "repeat(auto-fit, minmax(250px, 300px))" }}
                gap={{ base: 0, md: 4 }}
                overflowX={{ base: "auto", md: "visible" }}
                scrollSnapType={{ base: "x mandatory", md: "none" }}
                p={{ base: 0, md: 3 }}
                px={{ base: 0, md: 2 }}
                py={{ base: 2, md: 3 }}
                css={{
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {skeletonColumns.map((column, columnIndex) => (
                    <Box
                        key={columnIndex}
                        scrollSnapAlign={{ base: "start", md: "none" }}
                        display="flex"
                        flexDirection="column"
                        gap={3}
                        bg="transparent"
                        borderRadius="md"
                        p={3}
                    minW={{ base: "100vw", md: "250px" }}
                    maxW={{ base: "100vw", md: "none" }}
                    flex={{ base: "0 0 auto", md: "none" }}
                    px={{ base: 4, md: 0 }}
                    >
                        {column.map((_, itemIndex) => (
                            <Grid
                                key={itemIndex}
                                templateColumns="auto 1fr auto"
                                templateRows="1fr"
                                gap={2}
                                alignItems="center"
                                p={2}
                                bg="secondary.100"
                                borderRadius="md"
                                boxShadow="lg"
                                minH="70px"
                            w="100%"
                            >
                                <SkeletonCircle size="16" gridColumn="1/2" gridRow="1/4" alignSelf="center" />
                                <Box gridColumn="2/3" gridRow="1/4" justifySelf="flex-start" alignSelf="center">
                                    <Skeleton height="20px" width="120px" />
                                </Box>
                                <Skeleton height="32px" width="32px" gridColumn="3/4" gridRow="1/4" alignSelf="center" borderRadius="md" />
                            </Grid>
                        ))}
                    </Box>
                ))}
            </Box>
        );
    };

    if (loading) {
        return <LoadingSkeleton />;
    }

    if (data.length === 0) {
        return (
            <Box p={8} textAlign="center">
                <Text color="gray.500" fontSize="sm">
                    No top artists available yet
                </Text>
            </Box>
        );
    }

    const columns = createColumns(data);

    return (
        <Box
            display={{ base: "flex", md: "grid" }}
            gridTemplateColumns={{ md: "repeat(auto-fit, minmax(250px, 300px))", lg: "repeat(auto-fit, minmax(250px, 300px))", xl: "repeat(auto-fit, minmax(250px, 300px))" }}
            gap={{ base: 0, md: 4 }}
            overflowX={{ base: "auto", md: "visible" }}
            scrollSnapType={{ base: "x mandatory", md: "none" }}
            p={{ base: 0, md: 3 }}
            px={{ base: 0, md: 2 }}
            py={{ base: 2, md: 3 }}
            css={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}
        >
            {columns.map((column, columnIndex) => (
                <Box
                    key={columnIndex}
                    scrollSnapAlign={{ base: "start", md: "none" }}
                    display="flex"
                    flexDirection="column"
                    gap={3}
                    bg="transparent"
                    borderRadius="md"
                    p={3}
                    minW={{ base: "100%", md: "250px" }}
                    maxW={{ base: "100%", md: "none" }}
                    flex={{ base: "0 0 auto", md: "none" }}
                    px={{ base: 4, md: 0 }}
                >
                    {column.map((item) => (
                        <Grid
                            key={item.artist.user_id}
                            as={Link}
                            to={`/artist/${item.artist.user_id}`}
                            templateColumns="auto 1fr auto"
                            templateRows="1fr"
                            gap={2}
                            alignItems="center"
                            p={2}
                            bg="secondary.100"
                            borderRadius="md"
                            boxShadow="lg"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                            transition="all 0.3s ease"
                            minH="70px"
                            w="100%"
                            textDecoration="none"
                        >
                            <Box
                                gridColumn="1/2"
                                gridRow="1/4"
                                alignSelf="center"
                                position="relative"
                                transition="transform 0.3s ease-in-out"
                                _hover={{ transform: "scale(1.1)" }}
                            >
                                <Image 
                                    src={item.artist.account.profile.image_url} 
                                    alt={item.artist.account.profile.artist_name} 
                                    borderRadius="lg" 
                                    width={{ base: "4rem", md: "4rem" }} 
                                />
                            </Box>
                            
                            <Grid gridColumn="2/3" gridRow="1/4" justifySelf="flex-start" alignSelf="center">
                                <Text 
                                    fontSize="sm" 
                                    color="primary.100" 
                                    textOverflow="ellipsis" 
                                    overflow="hidden" 
                                    whiteSpace="nowrap"
                                >
                                    {item.artist.account.profile.artist_name}
                                </Text>
                            </Grid>
                            
                            <Box
                                gridColumn="3/4"
                                gridRow="1/4"
                                alignSelf="center"
                                justifySelf="flex-end"
                                mx={3}
                            >
                                <IconButton
                                    icon={<FaUserAlt />}
                                    variant="glassy"
                                    size="sm"
                                    aria-label={`View ${item.artist.account.profile.artist_name}'s Profile`}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

export default TopArtists;